<template>
  <div class="w-10/12">
    <div class="w-full" v-if="!showError">
      <div style="height: 100%" v-if="loading">
        <loader size="xxs" :loader-image="false" />
      </div>
      <div v-else class="mt-6 text-jet">
        <div class="w-full flex justify-between ">
          <p class="font-semibold mb-7 inline-block">Personal Information</p>
          <span class="float-right cursor-pointer text-flame text-base font-semibold">
            <span @click="handleClick" v-if="!edit">
              <icon icon-name="icon-edit" size="xs" class="inline-block" />
              <span class="pl-2">Edit</span>
            </span>
          </span>
        </div>

        <!-- Show message when employee cannot update their profile -->
        <div v-if="isEmployeeView && !canEmployeeUpdateProfile" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <icon icon-name="icon-warning" size="sm" class="text-yellow-400" />
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-800">
                You do not have permission to update your profile information. Please contact your administrator.
              </p>
            </div>
          </div>
        </div>
        <div class="w-full flex justify-between items-center">
          <p class="font-semibold">Bio Data</p>
          <div class="cursor-pointer" @click="isBioData = !isBioData">
            <icon :icon-name="isBioData ? 'chevronUp' : 'icon-arrow-down'" class="icon" size="xs"/>
          </div>
        </div>
        <div v-if="isBioData" class="grid grid-cols-12 gap-3 text-darkPurple" style="margin-top: 1.125rem">
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="First Name" v-model="employeeBasic.fname"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="Middle Name" v-model="employeeBasic.mname"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Last Name" v-model="employeeBasic.lname"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full h-10" :options="gender" label="Gender"
              v-model="employeeBasic.gender" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <label class="date-label">Date of Birth</label>
            <div :class="!edit ? 'date-flex_disabled' : 'date-flex'">
              <datepicker placeholder="--Enter--" :input-class="!edit ? 'date-input date-input_disabled' : 'date-input'
                " style="width: 100%; outline: none" v-model="employeeBasic.dob" :disabled="!edit" />
            </div>
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full h-10" label="Nationality" :options="countrySelect"
              @input="changeStateOrigin" v-model="employeeBasic.nationality" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full h-10"
              v-if="employeeBasic.nationality === 'NGA'"
              label="State of Origin"
              :options="$store.state.statesOrigin" v-model="employeeBasic.stateOfOrigin" :disabled="!edit"
              @onChange="changeLGA" />
            <c-text placeholder="--Enter--" v-else variant="w-full h-10"
              label="State of Origin"
              v-model="employeeBasic.stateOfOrigin" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <!-- <c-select placeholder="--Enter--" v-if="employeeBasic.nationality === 'NGA'" variant="w-full"
              label="Local Government Area" :options="lgaArray" v-model="employeeBasic.localGovernmentArea"
              :disabled="!edit" /> -->
            <c-text placeholder="--Enter--" variant="w-full"
              label="Local Government Area"
              v-model="employeeBasic.localGovernmentArea" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full h-10" label="Marital Status"
              :options="maritalStatusOptions" v-model="employeeBasic.maritalStatus" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full h-10" label="ID Type" :options="idType"
              v-model="employeeBasic.idType" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="ID Number" v-model="employeeBasic.idNumber"
              :disabled="!edit" />
          </div>
        </div>

        <div class="line"></div>

        <div class="w-full flex justify-between items-center">
          <p class="font-semibold">Contact Info</p>
          <div class="cursor-pointer" @click="isContactInfo = !isContactInfo">
            <icon :icon-name="isContactInfo ? 'chevronUp' : 'icon-arrow-down'" class="icon" size="xs"/>
          </div>
        </div>
        <div v-if="isContactInfo" class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full" :options="countrySelect" @input="changeState"
              label="Country" v-model="employeeBasic.country" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" v-if="employeeBasic.country === 'NGA'" variant="w-full h-10" label="State"
              :options="$store.state.states" v-model="employeeBasic.state" :disabled="!edit" />
            <c-text placeholder="--Enter--" v-else variant="w-full h-10" label="State" v-model="employeeBasic.state"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="City" v-model="employeeBasic.city"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="Address" v-model="employeeBasic.address"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="Unit" v-model="employeeBasic.unit"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Zip Code" type="number"
              v-model="employeeBasic.zip" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10"  label="Phone Number"
              v-model="employeeBasic.phoneNo" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <div class="text-sm mb-1 text-jet">Work Email</div>
            <div class="wrapper" v-tooltip.bottom-start="employeeBasic.email"
              :class="!edit ? 'disabled disabled_hideOutline' : 'disabled'">
              {{ employeeBasic.email }}
            </div>
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="Personal Email"
              v-model="employeeBasic.alternateEmail" :disabled="!edit" />
          </div>
        </div>

        <div class="line"></div>

        <div class="w-full flex justify-between items-center">
          <p class="font-semibold">Next of Kin</p>
          <div class="cursor-pointer" @click="isNextOfKin = !isNextOfKin">
            <icon :icon-name="isNextOfKin ? 'chevronUp' : 'icon-arrow-down'" class="icon" size="xs"/>
          </div>
        </div>
        <div v-if="isNextOfKin" class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="First Name" v-model="nextOfKinData.nokFname"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="Last Name" v-model="nextOfKinData.nokLname"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Phone Number" v-model="nextOfKinData.nokPhone"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Email" v-model="nextOfKinData.nokEmail"
              :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full h-10" :options="$store.state.nok" label="Relationship"
                v-model="nextOfKinData.nokRelationship" :disabled="!edit" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Address" v-model="nextOfKinData.nokAddress"
              :disabled="!edit" />
          </div>
          <!-- <div class="col-span-12" v-if="edit">
            <button @click="clearNextOfKin" type="button" class="text-red-600 hover:text-red-800 text-sm">
              Clear Next of Kin Information
            </button>
          </div> -->
        </div>

        <div class="line"></div>

        <div class="w-full flex justify-between items-center">
          <p class="font-semibold">Beneficiary</p>
          <div class="cursor-pointer" @click="isBeneficiary = !isBeneficiary">
            <icon :icon-name="isBeneficiary ? 'chevronUp' : 'icon-arrow-down'" class="icon" size="xs"/>
          </div>
        </div>
        <div class="my-1 mb-3" v-if="edit">
          <checkbox label="Same as Next of Kin" v-model="beneficiarySameAsNok"
            checkbox-size="height:16px; width:16px; margin-top:4px; cursor: pointer;" label-style="color:#333333"
          />
        </div>
        <div v-if="isBeneficiary" class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="First Name" v-model="beneficiaryData.beneficiaryFname"
              :disabled="!edit || beneficiarySameAsNok" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="Last Name" v-model="beneficiaryData.beneficiaryLname"
              :disabled="!edit || beneficiarySameAsNok" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Phone Number" v-model="beneficiaryData.beneficiaryPhone"
              :disabled="!edit || beneficiarySameAsNok" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Email" v-model="beneficiaryData.beneficiaryEmail"
              :disabled="!edit || beneficiarySameAsNok" />
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full h-10" :options="$store.state.nok" label="Relationship"
              v-model="beneficiaryData.beneficiaryRelationship" :disabled="!edit || beneficiarySameAsNok" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Address" v-model="beneficiaryData.beneficiaryAddress"
              :disabled="!edit || beneficiarySameAsNok" />
          </div>
          <!-- <div class="col-span-12" v-if="edit">
            <button @click="clearBeneficiary" type="button" class="text-red-600 hover:text-red-800 text-sm">
              Clear Beneficiary Information
            </button>
          </div> -->
        </div>

        <div class="line"></div>

        <div class="w-full flex justify-between items-center">
          <p class="font-semibold">Emergency contact</p>
          <div class="cursor-pointer" @click="isEmergencyContact = !isEmergencyContact">
            <icon :icon-name="isEmergencyContact ? 'chevronUp' : 'icon-arrow-down'" class="icon" size="xs"/>
          </div>
        </div>
        <div class="my-1 mb-3" v-if="edit">
          <checkbox label="Same as Next of Kin" v-model="emergencyContactSameAsNok"
            checkbox-size="height:16px; width:16px; margin-top:4px; cursor: pointer;" label-style="color:#333333"
          />
        </div>
        <div v-if="isEmergencyContact" class="grid grid-cols-12 gap-6 text-darkPurple" style="margin-top: 1.125rem">
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="First Name" v-model="emergencyContactData.emergencyContactFname"
              :disabled="!edit || emergencyContactSameAsNok" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full" label="Last Name" v-model="emergencyContactData.emergencyContactLname"
              :disabled="!edit || emergencyContactSameAsNok" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Phone Number" v-model="emergencyContactData.emergencyContactPhone"
              :disabled="!edit || emergencyContactSameAsNok" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Email" v-model="emergencyContactData.emergencyContactEmail"
              :disabled="!edit || emergencyContactSameAsNok" />
          </div>
          <div class="col-span-3">
            <c-select placeholder="--Enter--" variant="w-full h-10" :options="$store.state.nok" label="Relationship"
                v-model="emergencyContactData.emergencyContactRelationship" :disabled="!edit || emergencyContactSameAsNok" />
          </div>
          <div class="col-span-3">
            <c-text placeholder="--Enter--" variant="w-full h-10" label="Address" v-model="emergencyContactData.emergencyContactAddress"
              :disabled="!edit || emergencyContactSameAsNok" />
          </div>
          <!-- <div class="col-span-12" v-if="edit">
            <button @click="clearEmergency" type="button" class="text-red-600 hover:text-red-800 text-sm">
              Clear Emergency Contact Information
            </button>
          </div> -->
        </div>

        <div class="line"></div>

        <div class="mb-6" v-feature-request v-if="!isEmployeeView">
          <checkbox label="Allow employee updates" class="cursor-pointer"
            checkbox-size="height:16px; width:16px; margin-top:4px;" label-style="color:#333333"
            v-model="employeeBasic.canUpdateProfileInfo" />
        </div>

        <div class="line"></div>

        <div v-if="edit" class="w-full h-16 flex justify-start items-end gap-1 mt-4">
          <Button @click="handleSave"
            class="text-white bg-dynamicBackBtn w-36 h-11 flex justify-center items-center"
          >
            Save
          </Button>
          <Button @click="cancel"
            class="text-darkPurple bg-white w-20 h-11 flex justify-center items-center"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
    <error-page v-if="showError" />
  </div>
</template>

<script>
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import { mapState } from "vuex";
import Datepicker from "vuejs-datepicker";
import gender from "@/helpers/gender";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import ErrorPage from "@/modules/Admin/error403";

export default {
  name: "PersonalInfo",
  components: {
    CText,
    ErrorPage,
    CSelect,
    Datepicker,
    Checkbox,
    Button,
  },
  props: {
    showEdit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isBioData: true,
      isContactInfo: true,
      isNextOfKin: true,
      isBeneficiary: true,
      isEmergencyContact: true,
      beneficiarySameAsNok: false,
      emergencyContactSameAsNok: false,
      idType: [
        {
          id: "NationalId",
          name: "National ID",
        },
        {
          id: "InternationalPassportID",
          name: "International Passport ID",
        },
        {
          id: "DriversLicenseID",
          name: "Drivers License ID",
        },
      ],
      breadcrumbs: [
        {
          disabled: false,
          text: "Employees",
          href: "Employees",
          id: "Employees",
        },
        {
          disabled: false,
          text: "Employee Directory",
          href: "Employee Directory",
          id: "Employee Directory",
        },
        {
          disabled: false,
          text: "View Employee Profile",
          id: "View Employee Profile",
        },
      ],
      maritalStatusOptions: [
        { id: "Single", name: "Single" },
        { id: "Married", name: "Married" },
        { id: "Separated", name: "Separated" },
        { id: "Divorced", name: "Divorced" },
        { id: "Widowed", name: "Widowed" },
        { id: "In a Relationship", name: "In a Relationship" },
        { id: "Engaged", name: "Engaged" },
        { id: "Domestic Partnership", name: "Domestic Partnership" },
        { id: "Civil Union", name: "Civil Union" },
        { id: "Prefer not to say", name: "Prefer not to say" }
      ],
      isDisabled: true,
      edit: false,
      gender,
      photo: "",
      email: "",
      error: [],
      countrySelect: [],
      allowNextPage: false,
      showStates: true,
      showStatesOfOrigin: false,
      lgaArray: [],
      showError: false,
      employeeBasic: {
        id: null,
        fname: "",
        mname: "",
        lname: "",
        gender: "",
        dob: "",
          phoneNo: null,
        email: null,
        alternateEmail: null,
        nationality: null,
        stateOfOrigin: null,
        stateOfOriginId: null,
        localGovernmentArea: null,
        localGovernmentAreaId: null,
        maritalStatus: null,
        idType: null,
        idNumber: "",
        address: null,
        country: null,
        city: null,
        state: null,
        zip: "",
        unit: null,
        canUpdateProfileInfo: false,
      },
      // Pre-define all next of kin, beneficiary, and emergency contact properties
      nextOfKinData: {
        nokFname: '',
        nokLname: '',
        nokPhone: '',
        nokEmail: '',
        nokRelationship: '',
        nokAddress: ''
      },
      beneficiaryData: {
        beneficiaryFname: '',
        beneficiaryLname: '',
        beneficiaryPhone: '',
        beneficiaryEmail: '',
        beneficiaryRelationship: '',
        beneficiaryAddress: '',
        beneficiaryIsNextOfKin: false
      },
      emergencyContactData: {
        emergencyContactFname: '',
        emergencyContactLname: '',
        emergencyContactPhone: '',
        emergencyContactEmail: '',
        emergencyContactRelationship: '',
        emergencyContactAddress: '',
        emergencyContactIsNextOfKin: false
      }
    };
  },
  computed: {
    ...mapState({
      employeePhoto: (state) => state.employee.employeePhoto,
      loading: (state) => state.employee.loading,
    }),
    canEmployeeUpdateProfile() {
      return this.employeeBasic.canUpdateProfileInfo === true;
    },
    isEmployeeView() {
      return this.$route.query.slug === 'employee' || this.$route.path.includes('/ess/');
    },
  },
  watch: {
    beneficiarySameAsNok(newValue) {
      if (newValue) {
        this.beneficiaryData.beneficiaryFname = this.nextOfKinData.nokFname || '';
        this.beneficiaryData.beneficiaryLname = this.nextOfKinData.nokLname || '';
        this.beneficiaryData.beneficiaryPhone = this.nextOfKinData.nokPhone || '';
        this.beneficiaryData.beneficiaryEmail = this.nextOfKinData.nokEmail || '';
        this.beneficiaryData.beneficiaryRelationship = this.nextOfKinData.nokRelationship || '';
        this.beneficiaryData.beneficiaryAddress = this.nextOfKinData.nokAddress || '';
        this.beneficiaryData.beneficiaryIsNextOfKin = true;
      } else {
        this.beneficiaryData.beneficiaryFname = '';
        this.beneficiaryData.beneficiaryLname = '';
        this.beneficiaryData.beneficiaryPhone = '';
        this.beneficiaryData.beneficiaryEmail = '';
        this.beneficiaryData.beneficiaryRelationship = '';
        this.beneficiaryData.beneficiaryAddress = '';
        this.beneficiaryData.beneficiaryIsNextOfKin = false;
      }
    },
    emergencyContactSameAsNok(newValue) {
      if (newValue) {
        // Copy current next of kin data (whether from backend or fresh input)
        this.emergencyContactData.emergencyContactFname = this.nextOfKinData.nokFname || '';
        this.emergencyContactData.emergencyContactLname = this.nextOfKinData.nokLname || '';
        this.emergencyContactData.emergencyContactPhone = this.nextOfKinData.nokPhone || '';
        this.emergencyContactData.emergencyContactEmail = this.nextOfKinData.nokEmail || '';
        this.emergencyContactData.emergencyContactRelationship = this.nextOfKinData.nokRelationship || '';
        this.emergencyContactData.emergencyContactAddress = this.nextOfKinData.nokAddress || '';
        this.emergencyContactData.emergencyContactIsNextOfKin = true;
      } else {
        this.emergencyContactData.emergencyContactFname = '';
        this.emergencyContactData.emergencyContactLname = '';
        this.emergencyContactData.emergencyContactPhone = '';
        this.emergencyContactData.emergencyContactEmail = '';
        this.emergencyContactData.emergencyContactRelationship = '';
        this.emergencyContactData.emergencyContactAddress = '';
        this.emergencyContactData.emergencyContactIsNextOfKin = false;
      }
    },
    'nextOfKinData.nokFname': {
      handler() { this.syncFromNok(); }
    },
    'nextOfKinData.nokLname': {
      handler() { this.syncFromNok(); }
    },
    'nextOfKinData.nokPhone': {
      handler() { this.syncFromNok(); }
    },
    'nextOfKinData.nokEmail': {
      handler() { this.syncFromNok(); }
    },
    'nextOfKinData.nokRelationship': {
      handler() { this.syncFromNok(); }
    },
    'nextOfKinData.nokAddress': {
      handler() { this.syncFromNok(); }
    },
    'employeeBasic.nationality': function(newVal) {
      if (newVal === 'NGA') {
        this.showStatesOfOrigin = true;
        // If state of origin exists, load LGA
        if (this.employeeBasic.stateOfOrigin) {
          this.changeLGA(this.employeeBasic.stateOfOrigin);
        }
      } else {
        this.showStatesOfOrigin = false;
        // Clear state of origin and LGA when not Nigeria
        this.employeeBasic.stateOfOrigin = null;
        this.employeeBasic.localGovernmentArea = '';
        this.lgaArray = [];
      }
    },
    'employeeBasic.stateOfOrigin': {
      handler(newVal) {
        if (newVal && this.employeeBasic.nationality === 'NGA') {
          this.changeLGA(newVal);
        } else {
          this.lgaArray = [];
        }
      }
    },
    'employeeBasic.id': function(newVal) {
      if (newVal) {
          this.$nextTick(() => {
            this.initializeDataFromBackend();
          });
        }
    },
    '$route.params.id': {
      async handler(newId, oldId) {
        if (newId && newId !== oldId) {
          try {
            await this.$store.dispatch("employee/getOneEmployeeAll", newId);
            if (this.$store.state.employee.allEmployeeDetails) {
              this.employeeBasic = {
                ...this.employeeBasic,
                ...this.$store.state.employee.allEmployeeDetails,
              };
            }
            this.initializeDataFromBackend();
            // Initialize LGA options if nationality is NGA and state of origin exists
            if (this.employeeBasic && this.employeeBasic.nationality === 'NGA' && this.employeeBasic.stateOfOrigin) {
              this.changeLGA(this.employeeBasic.stateOfOrigin);
            }
          } catch (error) {
            throw new Error(error.response);
         }
        }
      },
      immediate: false
    },
  },
  methods: {
    async handleClick() {
      if (this.isEmployeeView && !this.canEmployeeUpdateProfile) {
        this.$toasted.error("You do not have permission to update your profile information", {
          duration: 5000,
        });
        return;
      }

      if (this.$route.query.slug) {
        this.edit = !this.edit;
        return;
      }

      try {
        await this.$handlePrivilege(
          "employeeDirectory",
          "editEmployeePersonalInfo"
        );
        this.edit = !this.edit;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    changeLGA(value) {
      this.lgaArray = [];
      this.$_getLocalGovernmentArears(value).then(({ data }) => {
        this.lgaArray = data.data.map((element) => ({
          id: element.id,
          name: element.area,
        }));
      }).catch(() => {});
    },
    async handleSave() {
      this.employeeBasic.containsBio = true;
      if (this.employeePhoto) {
        this.employeeBasic.photo = this.employeePhoto;
      }

      // Ensure canUpdateProfileInfo and checkbox states are included in the payload
      const payload = {
        id: this.$route.params.id,
        type: "profile",
        payload: {
          ...this.employeeBasic,
          // Explicitly include local data objects to ensure they're sent to backend
          ...this.nextOfKinData,
          ...this.beneficiaryData,
          ...this.emergencyContactData,
          canUpdateProfileInfo: this.employeeBasic.canUpdateProfileInfo !== undefined
            ? this.employeeBasic.canUpdateProfileInfo
            : false,
          beneficiaryIsNextOfKin: this.beneficiarySameAsNok,
          emergencyContactIsNextOfKin: this.emergencyContactSameAsNok
        },
      };

      try {
        await this.$_editEmployeeByType(payload)
        const message = "Personal info, updated successfully!";
        this.edit = false;
        this.$store.dispatch("employee/updateEmployeePhoto", null);
        return this.$toasted.success(message, { duration: 3000 });
      } catch (error) {
        await this.$store.dispatch("employee/getOneEmployeeAll", this.$route.params.id);
        this.edit = false;
        this.$store.dispatch("employee/updateEmployeePhoto", null);
        return this.$toasted.error(error, { duration: 3000 });
      }
    },
    changeStateOrigin(value) {
      if (value === "NGA") {
        this.showStatesOfOrigin = true;
        // If state of origin exists, load LGA
        if (this.employeeBasic.stateOfOrigin) {
          this.changeLGA(this.employeeBasic.stateOfOrigin);
        }
      } else {
        this.showStatesOfOrigin = false;
        // Clear state of origin and LGA when not Nigeria
        this.employeeBasic.stateOfOrigin = '';
        this.employeeBasic.localGovernmentArea = '';
        this.lgaArray = [];
      }
    },
    changeState(value) {
      if (value === "NGA") {
        this.showStates = true;
      } else {
        this.showStates = false;
      }
    },
    cancel() {
      this.$router.push({
        name: "EmployeeDirectory",
      });
    },
    clearBeneficiary() {
      this.beneficiaryData.beneficiaryFname = '';
      this.beneficiaryData.beneficiaryLname = '';
      this.beneficiaryData.beneficiaryPhone = '';
      this.beneficiaryData.beneficiaryEmail = '';
      this.beneficiaryData.beneficiaryRelationship = '';
      this.beneficiaryData.beneficiaryAddress = '';
      this.beneficiaryData.beneficiaryIsNextOfKin = false;
      this.beneficiarySameAsNok = false;
    },
    clearEmergency() {
      this.emergencyContactData.emergencyContactFname = '';
      this.emergencyContactData.emergencyContactLname = '';
      this.emergencyContactData.emergencyContactPhone = '';
      this.emergencyContactData.emergencyContactEmail = '';
      this.emergencyContactData.emergencyContactRelationship = '';
      this.emergencyContactData.emergencyContactAddress = '';
      this.emergencyContactData.emergencyContactIsNextOfKin = false;
      this.emergencyContactSameAsNok = false;
    },

    clearNextOfKin() {
      this.nextOfKinData.nokFname = '';
      this.nextOfKinData.nokLname = '';
      this.nextOfKinData.nokPhone = '';
      this.nextOfKinData.nokEmail = '';
      this.nextOfKinData.nokRelationship = '';
      this.nextOfKinData.nokAddress = '';

      // Clear beneficiary and emergency contact if they were synced
      if (this.beneficiarySameAsNok) {
        this.beneficiaryData.beneficiaryFname = '';
        this.beneficiaryData.beneficiaryLname = '';
        this.beneficiaryData.beneficiaryPhone = '';
        this.beneficiaryData.beneficiaryEmail = '';
        this.beneficiaryData.beneficiaryRelationship = '';
        this.beneficiaryData.beneficiaryAddress = '';
        this.beneficiaryData.beneficiaryIsNextOfKin = false;
        this.beneficiarySameAsNok = false;
      }

      if (this.emergencyContactSameAsNok) {
        this.emergencyContactData.emergencyContactFname = '';
        this.emergencyContactData.emergencyContactLname = '';
        this.emergencyContactData.emergencyContactPhone = '';
        this.emergencyContactData.emergencyContactEmail = '';
        this.emergencyContactData.emergencyContactRelationship = '';
        this.emergencyContactData.emergencyContactAddress = '';
        this.emergencyContactData.emergencyContactIsNextOfKin = false;
        this.emergencyContactSameAsNok = false;
      }
    },
    syncFromNok() {
      if (this.beneficiarySameAsNok) {
        this.beneficiaryData.beneficiaryFname = this.nextOfKinData.nokFname || '';
        this.beneficiaryData.beneficiaryLname = this.nextOfKinData.nokLname || '';
        this.beneficiaryData.beneficiaryPhone = this.nextOfKinData.nokPhone || '';
        this.beneficiaryData.beneficiaryEmail = this.nextOfKinData.nokEmail || '';
        this.beneficiaryData.beneficiaryRelationship = this.nextOfKinData.nokRelationship || '';
        this.beneficiaryData.beneficiaryAddress = this.nextOfKinData.nokAddress || '';
      }

      // Sync emergency contact if checkbox is checked
      if (this.emergencyContactSameAsNok) {
        this.emergencyContactData.emergencyContactFname = this.nextOfKinData.nokFname || '';
        this.emergencyContactData.emergencyContactLname = this.nextOfKinData.nokLname || '';
        this.emergencyContactData.emergencyContactPhone = this.nextOfKinData.nokPhone || '';
        this.emergencyContactData.emergencyContactEmail = this.nextOfKinData.nokEmail || '';
        this.emergencyContactData.emergencyContactRelationship = this.nextOfKinData.nokRelationship || '';
        this.emergencyContactData.emergencyContactAddress = this.nextOfKinData.nokAddress || '';
      }
    },
    initializeDataFromBackend() {
      // Populate local data objects with backend data
      this.nextOfKinData.nokFname = this.employeeBasic.nokFname || '';
      this.nextOfKinData.nokLname = this.employeeBasic.nokLname || '';
      this.nextOfKinData.nokPhone = this.employeeBasic.nokPhone || '';
      this.nextOfKinData.nokEmail = this.employeeBasic.nokEmail || '';
      this.nextOfKinData.nokRelationship = this.employeeBasic.nokRelationship || '';
      this.nextOfKinData.nokAddress = this.employeeBasic.nokAddress || '';

      this.beneficiaryData.beneficiaryFname = this.employeeBasic.beneficiaryFname || '';
      this.beneficiaryData.beneficiaryLname = this.employeeBasic.beneficiaryLname || '';
      this.beneficiaryData.beneficiaryPhone = this.employeeBasic.beneficiaryPhone || '';
      this.beneficiaryData.beneficiaryEmail = this.employeeBasic.beneficiaryEmail || '';
      this.beneficiaryData.beneficiaryRelationship = this.employeeBasic.beneficiaryRelationship || '';
      this.beneficiaryData.beneficiaryAddress = this.employeeBasic.beneficiaryAddress || '';
      this.beneficiaryData.beneficiaryIsNextOfKin = this.employeeBasic.beneficiaryIsNextOfKin || false;

      this.emergencyContactData.emergencyContactFname = this.employeeBasic.emergencyContactFname || '';
      this.emergencyContactData.emergencyContactLname = this.employeeBasic.emergencyContactLname || '';
      this.emergencyContactData.emergencyContactPhone = this.employeeBasic.emergencyContactPhone || '';
      this.emergencyContactData.emergencyContactEmail = this.employeeBasic.emergencyContactEmail || '';
      this.emergencyContactData.emergencyContactRelationship = this.employeeBasic.emergencyContactRelationship || '';
      this.emergencyContactData.emergencyContactAddress = this.employeeBasic.emergencyContactAddress || '';
      this.emergencyContactData.emergencyContactIsNextOfKin = this.employeeBasic.emergencyContactIsNextOfKin || false;

      // Initialize checkbox values based on existing data
      this.beneficiarySameAsNok = this.employeeBasic.beneficiaryIsNextOfKin || false;
      this.emergencyContactSameAsNok = this.employeeBasic.emergencyContactIsNextOfKin || false;

      // If checkboxes are checked but data is missing, sync from next of kin
      if (this.beneficiarySameAsNok && (!this.beneficiaryData.beneficiaryFname || !this.beneficiaryData.beneficiaryLname) && this.nextOfKinData.nokFname) {
        this.beneficiaryData.beneficiaryFname = this.nextOfKinData.nokFname;
        this.beneficiaryData.beneficiaryLname = this.nextOfKinData.nokLname;
        this.beneficiaryData.beneficiaryPhone = this.nextOfKinData.nokPhone;
        this.beneficiaryData.beneficiaryEmail = this.nextOfKinData.nokEmail;
        this.beneficiaryData.beneficiaryRelationship = this.nextOfKinData.nokRelationship;
        this.beneficiaryData.beneficiaryAddress = this.nextOfKinData.nokAddress;
      }

      if (this.emergencyContactSameAsNok && (!this.emergencyContactData.emergencyContactFname || !this.emergencyContactData.emergencyContactLname) && this.nextOfKinData.nokFname) {
        this.emergencyContactData.emergencyContactFname = this.nextOfKinData.nokFname;
        this.emergencyContactData.emergencyContactLname = this.nextOfKinData.nokLname;
        this.emergencyContactData.emergencyContactPhone = this.nextOfKinData.nokPhone;
        this.emergencyContactData.emergencyContactEmail = this.nextOfKinData.nokEmail;
        this.emergencyContactData.emergencyContactRelationship = this.nextOfKinData.nokRelationship;
        this.emergencyContactData.emergencyContactAddress = this.nextOfKinData.nokAddress;
      }
    },
  },

  async mounted() {
    try {
      if (!this.$route.query.slug) {
        await this.$handlePrivilege(
          "employeeDirectory",
          "viewEmployeePersonalInfo"
        );
      }

      // Fetch employee data if we have an employee ID
      if (this.$route.params.id) {
        await this.$store.dispatch("employee/getOneEmployeeAll", this.$route.params.id);
      }

      this.$store.dispatch("employee/updateEmployeePhoto", null);
      this.countrySelect = this.$store.state.countries.map((element) => ({
        id: element.Alpha3Code,
        name: element.Name,
      }));

      if (this.$store.state.employee.allEmployeeDetails) {
        this.employeeBasic = {
          ...this.employeeBasic,
          ...this.$store.state.employee.allEmployeeDetails,
        };
      }

      this.initializeDataFromBackend();

      // Initialize LGA options if nationality is NGA and state of origin exists
      if (this.employeeBasic && this.employeeBasic.nationality === 'NGA' && this.employeeBasic.stateOfOrigin) {
        this.changeLGA(this.employeeBasic.stateOfOrigin);
      }
    } catch (error) {
      this.showError = true;
    }
  },
};
</script>

<style scoped>
.wrapper {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.line{
  border: 1px solid #C2C7D6;
  width: 100%;
  height: 0;
  margin: 30px auto;
  border-radius: 10px;
  padding: 0;
}
.icon{
  width: 20px !important;
  height: 15px !important;
}
</style>